import { useState, useEffect } from "react";
import {
  FaTachometerAlt,
  FaUsers,
  FaUserSlash,
  FaCar,
  FaClipboardCheck,
  FaClock,
  FaBan,
  FaRoad,
  FaMapMarkerAlt,
  FaFileAlt,
  FaBell,
  FaCog,
  FaUser,
  FaUserCheck,
  FaFileInvoiceDollar,
  FaWallet,
  FaHeadset,
  FaAd,
} from "react-icons/fa";
import { GiSteeringWheel } from "react-icons/gi";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { GetUser } from "../Services";

const SideNav = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [active, setActive] = useState("dashboard");
  const [isAdmin, setIsAdmin] = useState(false);

  // ⚛️ NEW STATE FOR REACT-DRIVEN DROPDOWN CONTROL
  const [dropdownOpen, setDropdownOpen] = useState({});

  useEffect(() => {
    setIsAdmin(user && user.role === "ADMIN");
  }, [user]);

  // Helper to toggle dropdown state
  const toggleDropdown = (key) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Update active state based on current route and open the corresponding dropdown
  useEffect(() => {
    const path = location.pathname;
    let newActive = "dashboard";
    const newDropdownState = {};

    // --- Route to Active State Mapping ---
    const pathMap = {
      "/": "dashboard",
      "/dispatchers": "dispatchers",
      "/passengers": "passengers",
      // Drivers Dropdown
      "/drivers": "drivers",
      "/online-drivers": "online-drivers",
      "/unapproved-drivers": "unapproved-drivers",
      // Rides Dropdown
      "/rides": "rides",
      "/pending-rides": "pending-rides",
      "/canceled-rides": "canceled-rides",
      // Booking Dropdown
      "/dispatch": "dispatch",
      "/booking": "booking",
      // Vehicle Management Dropdown
      "/vehicle-list": "vehicle-list",
      "/vehicle-type": "vehicle-type",
      "/service-type": "service-type",

      // Other top-level links
      "/track-users": "track-users",
      "/document-list": "document-list",
      // Payment Dropdown
      "/driver-payment": "driver-payment",
      "/admin-payment": "admin-payment",
      "/payment-report": "payment-report",
      // Statement Dropdown
      "/overall-statement": "overall-statement",
      "/notifications": "notifications",
      "/setting": "setting",
    };

    newActive = pathMap[path] || "dashboard";
    setActive(newActive);

    // --- Determine which dropdown should be open on route change ---
    const parentMap = {
      drivers: ["drivers", "online-drivers", "unapproved-drivers"],
      rides: ["rides", "pending-rides", "canceled-rides"],
      booking: ["dispatch", "booking"],
      vehicle: ["vehicle-list", "vehicle-type", "service-type"],
      payment: ["driver-payment", "admin-payment", "payment-report"],
      statement: ["overall-statement"],
    };

    // Open the parent dropdown if the new active link is a child
    for (const parentKey in parentMap) {
      if (parentMap[parentKey].includes(newActive)) {
        newDropdownState[parentKey] = true;
      } else {
        // Ensure other parents are closed, unless they were manually opened
        // by clicking the menu link itself. This line is optional,
        // but helps close unrelated menus on navigation.
        newDropdownState[parentKey] = false;
      }
    }

    // Only set the state if there's a difference to avoid unnecessary renders
    setDropdownOpen((prev) => {
      const hasChanged = Object.keys(newDropdownState).some(
        (key) => prev[key] !== newDropdownState[key],
      );
      return hasChanged ? { ...prev, ...newDropdownState } : prev;
    });
  }, [location.pathname]); // Dependency on route change

  const handleLogout = () => {
    GetUser.Logout(dispatch);
  };

  return (
    <div>
      <aside className="main-sidebar sidebar-dark-primary elevation-4">
        {/* Brand Logo */}
        <Link to="/" className="brand-link">
          <img
            src="dist/img/BINBERU.png"
            alt="AdminLTE Logo"
            className="brand-image img-circle elevation-3"
            style={{ opacity: ".8" }}
          />
          <span className="brand-text font-weight-light">BINBERU Admin</span>
        </Link>
        {/* Sidebar */}
        <div className="sidebar">
          {/* Sidebar user panel */}
          <div className="user-panel mt-3 pb-3 mb-3 d-flex">
            <div className="image">
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  className="img-circle elevation-2"
                  alt="User Image"
                />
              ) : (
                <img
                  src="dist/img/avatar.png"
                  className="img-circle elevation-2"
                  alt="User Image"
                />
              )}
            </div>
            <div className="info">
              <Link to="/profile" className="d-block">
                {user.name}
              </Link>
            </div>
          </div>
          {/* SidebarSearch Form */}
          <div className="form-inline">
            <div className="input-group" data-widget="sidebar-search">
              <input
                className="form-control form-control-sidebar"
                type="search"
                placeholder="Search"
                aria-label="Search"
              />
              <div className="input-group-append">
                <button className="btn btn-sidebar">
                  <i className="fas fa-search fa-fw" />
                </button>
              </div>
            </div>
          </div>
          {/* Sidebar Menu */}
          <nav className="mt-2">
            <ul
              className="nav nav-pills nav-sidebar flex-column"
              data-widget="treeview"
              role="menu"
              data-accordion="false"
            >
              {/* Dashboard */}
              <li className="nav-item">
                <Link
                  to="/"
                  className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                    active === "dashboard" ? "active bg-gray-700" : ""
                  }`}
                >
                  <FaTachometerAlt className="nav-icon" />
                  <p>Dashboard</p>
                </Link>
              </li>
              {isAdmin && (
                <>
                  <li className="nav-item">
                    <Link
                      to="/dispatchers"
                      className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                        active === "dispatchers" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaHeadset className="nav-icon" />
                      <p>Dispatcher</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="/passengers"
                      className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                        active === "passengers" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaUsers className="nav-icon" />
                      <p>Passenger</p>
                    </Link>
                  </li>
                  {/* Drivers Dropdown */}
                  <li
                    className={`nav-item ${
                      dropdownOpen.drivers ? "menu-open" : ""
                    }`}
                  >
                    {/* Updated Link with onClick handler */}
                    <Link
                      to="#"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        [
                          "drivers",
                          "online-drivers",
                          "unapproved-drivers",
                        ].includes(active)
                          ? "active bg-gray-700"
                          : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleDropdown("drivers");
                      }}
                    >
                      <GiSteeringWheel className="nav-icon" />
                      <p>
                        Drivers
                        <i className="fas fa-angle-left right" />
                      </p>
                    </Link>
                    <ul className="nav nav-treeview">
                      <li className="nav-item ml-4">
                        <Link
                          to="/drivers"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "drivers" ? "active bg-gray-700" : ""
                          }`}
                        >
                          <FaUser className="nav-icon" />
                          <p>Driver Manager</p>
                        </Link>
                      </li>
                      <li className="nav-item ml-4">
                        <Link
                          to="/online-drivers"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "online-drivers"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaUserCheck className="nav-icon" />
                          <p>Drivers Activity</p>
                        </Link>
                      </li>
                      <li className="nav-item ml-4">
                        <Link
                          to="/unapproved-drivers"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "unapproved-drivers"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaUserSlash className="nav-icon" />
                          <p>Un-Approved Driver</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                </>
              )}
              {/* Rides Dropdown */}
              <li
                className={`nav-item ${dropdownOpen.rides ? "menu-open" : ""}`}
              >
                {/* Updated Link with onClick handler */}
                <Link
                  to="#"
                  className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                    ["rides", "pending-rides", "canceled-rides"].includes(
                      active,
                    )
                      ? "active bg-gray-700"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown("rides");
                  }}
                >
                  <FaRoad className="nav-icon" />
                  <p>
                    Rides
                    <i className="fas fa-angle-left right" />
                  </p>
                </Link>
                <ul className="nav nav-treeview">
                  <li className="nav-item ml-4">
                    <Link
                      to="/rides"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "rides" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaCar className="nav-icon" />
                      <p>All Rides</p>
                    </Link>
                  </li>
                  <li className="nav-item ml-4">
                    <Link
                      to="/pending-rides"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "pending-rides" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaClock className="nav-icon" />
                      <p>Completed Rides</p>
                    </Link>
                  </li>
                  <li className="nav-item ml-4">
                    <Link
                      to="/canceled-rides"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "canceled-rides" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaBan className="nav-icon" />
                      <p>Canceled Rides</p>
                    </Link>
                  </li>
                </ul>
              </li>
              {/* Booking Dropdown */}
              <li
                className={`nav-item ${
                  dropdownOpen.booking ? "menu-open" : ""
                }`}
              >
                {/* Updated Link with onClick handler */}
                <Link
                  to="#"
                  className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                    ["dispatch", "booking"].includes(active)
                      ? "active bg-gray-700"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown("booking");
                  }}
                >
                  <FaClipboardCheck className="nav-icon" />
                  <p>
                    Booking
                    <i className="fas fa-angle-left right" />
                  </p>
                </Link>
                <ul className="nav nav-treeview">
                  <li className="nav-item ml-4">
                    <Link
                      to="/dispatch"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "dispatch" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaClipboardCheck className="nav-icon" />
                      <p>Manual Booking</p>
                    </Link>
                  </li>
                  <li className="nav-item ml-4">
                    <Link
                      to="/booking"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "booking" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaClock className="nav-icon" />
                      <p>Schedule</p>
                    </Link>
                  </li>
                </ul>
              </li>
              {/* Vehicle Management Dropdown */}
              {isAdmin && (
                <>
                  <li
                    className={`nav-item ${
                      dropdownOpen.vehicle ? "menu-open" : ""
                    }`}
                  >
                    {/* Updated Link with onClick handler */}
                    <Link
                      to="#"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        ["vehicle-list", "vehicle-type"].includes(active)
                          ? "active bg-gray-700"
                          : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleDropdown("vehicle");
                      }}
                    >
                      <FaRoad className="nav-icon" />
                      <p>
                        Vehicle Management
                        <i className="fas fa-angle-left right" />
                      </p>
                    </Link>
                    <ul className="nav nav-treeview">
                      <li className="nav-item ml-4">
                        <Link
                          to="/vehicle-list"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "vehicle-list"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaCar className="nav-icon" />
                          <p>Vehicles Manager</p>
                        </Link>
                      </li>
                      <li className="nav-item ml-4">
                        <Link
                          to="/vehicle-type"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "vehicle-type"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaClipboardCheck className="nav-icon" />
                          <p>Vehicle Type</p>
                        </Link>
                      </li>

                      <li className="nav-item ml-4">
                        <Link
                          to="/service-type"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "service-type"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaTachometerAlt className="nav-icon" />
                          <p>Service Category</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                </>
              )}

              {/* <li className="nav-item">
                <Link
                  to="/document-list"
                  className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                    active === "document-list" ? "active bg-gray-700" : ""
                  }`}
                >
                  <FaFileAlt className="nav-icon" />
                  <p>Manage Documents</p>
                </Link>
              </li> */}

              {/* Payment Dropdown */}
              <li
                className={`nav-item ${
                  dropdownOpen.payment ? "menu-open" : ""
                }`}
              >
                {/* Updated Link with onClick handler */}
                <Link
                  to="#"
                  className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                    [
                      "driver-payment",
                      "admin-payment",
                      "payment-report",
                    ].includes(active)
                      ? "active bg-gray-700"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown("payment");
                  }}
                >
                  <FaWallet className="nav-icon" />
                  <p>
                    Payment
                    <i className="fas fa-angle-left right" />
                  </p>
                </Link>
                <ul className="nav nav-treeview">
                  <li className="nav-item ml-4">
                    <Link
                      to="/driver-payment"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "driver-payment" ? "active bg-gray-700" : ""
                      }`}
                    >
                      <FaUser className="nav-icon" />
                      <p>Driver Payment</p>
                    </Link>
                  </li>
                  {isAdmin && (
                    <>
                      <li className="nav-item ml-4">
                        <Link
                          to="/admin-payment"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "admin-payment"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaUserCheck className="nav-icon" />
                          <p>Admin Payment</p>
                        </Link>
                      </li>
                      <li className="nav-item ml-4">
                        <Link
                          to="/payment-report"
                          className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                            active === "payment-report"
                              ? "active bg-gray-700"
                              : ""
                          }`}
                        >
                          <FaFileInvoiceDollar className="nav-icon" />
                          <p>Payment Report</p>
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </li>
              <li className="nav-item">
                <Link
                  to="/track-users"
                  className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                    active === "track-users" ? "active bg-gray-700" : ""
                  }`}
                >
                  <FaMapMarkerAlt className="nav-icon" />
                  <p>Track Users</p>
                </Link>
              </li>
              {isAdmin && (
                <li className="nav-item">
                  <Link
                    to="/document-list"
                    className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                      active === "document-list" ? "active bg-gray-700" : ""
                    }`}
                  >
                    <FaFileAlt className="nav-icon" />
                    <p>Manage Documents</p>
                  </Link>
                </li>
              )}
              {/* Statement Dropdown */}
              <li
                className={`nav-item ${
                  dropdownOpen.statement ? "menu-open" : ""
                }`}
              >
                {/* Updated Link with onClick handler */}
                <Link
                  to="#"
                  className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                    active === "overall-statement" ? "active bg-gray-700" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown("statement");
                  }}
                >
                  <FaWallet className="nav-icon" />
                  <p>
                    Statement
                    <i className="fas fa-angle-left right" />
                  </p>
                </Link>
                <ul className="nav nav-treeview">
                  <li className="nav-item ml-4">
                    <Link
                      to="/overall-statement"
                      className={`nav-link flex items-center gap-2 p-2 hover:bg-gray-700 rounded ${
                        active === "overall-statement"
                          ? "active bg-gray-700"
                          : ""
                      }`}
                    >
                      <FaUser className="nav-icon" />
                      <p>Overall Statement</p>
                    </Link>
                  </li>
                </ul>
              </li>
              <li className="nav-item">
                <Link
                  to="/notification"
                  className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                    active === "notifications" ? "active bg-gray-700" : ""
                  }`}
                >
                  <FaBell className="nav-icon" />
                  <p>Notifications</p>
                </Link>
              </li>
              {isAdmin && (
                <li className="nav-item">
                  <Link
                    to="/setting"
                    className={`flex items-center gap-2 p-2 nav-link hover:bg-gray-700 rounded ${
                      active === "setting" ? "active bg-gray-700" : ""
                    }`}
                  >
                    <FaCog className="nav-icon" />
                    <p>Settings</p>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
          {/* Logout Button */}
          <div className="mt-3">
            <button
              className="btn btn-sidebar w-100 text-left p-2 hover:bg-gray-700 rounded"
              onClick={handleLogout}
            >
              <i className="fas fa-sign-out-alt nav-icon mr-2" />
              Log Out
            </button>
          </div>
        </div>
        {/* /.sidebar */}
      </aside>
    </div>
  );
};

export default SideNav;
